import React from 'react';
import { Search, User, Phone, ChevronRight, Upload } from 'lucide-react';
import { theme, inputStyle } from '../theme';
import PatientProfile from './PatientProfile';

const goToMigration = () => {
  if (typeof window !== 'undefined' && typeof window.__setActiveTab === 'function') {
    window.__setActiveTab('data-migration');
  }
};

/**
 * Patients tab — lean directory when no patient selected; delegates full profile
 * rendering to <PatientProfile/> when one is.
 *
 * The directory view (this file) is intentionally small — all profile logic
 * lives in PatientProfile.jsx.
 */
const PatientsPage = ({ app }) => {
  const { patientsList, searchTerm, setSearchTerm, selectedPatient, setSelectedPatient } = app;

  if (selectedPatient) {
    return <PatientProfile app={app} />;
  }

  const filtered = (patientsList || []).filter(p => {
    const q = (searchTerm || '').toLowerCase();
    if (!q) return true;
    return (p.name || '').toLowerCase().includes(q)
      || (p.uhid || '').toLowerCase().includes(q)
      || (p.phone || '').includes(q);
  });

  return (
    <div style={{ background: theme.surface, borderRadius: 14, border: `1px solid ${theme.border}`, boxShadow: theme.shadowSm, padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, color: theme.navy, fontSize: 22, letterSpacing: '-0.01em' }}>Patients</h2>
          <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 4 }}>
            {filtered.length} of {patientsList.length} patients
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={goToMigration}
            title="Excel / CSV se patient list import karo"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '9px 14px', borderRadius: 8,
              background: theme.primaryLight, color: theme.primaryDark,
              border: `1px solid ${theme.primary}`,
              fontWeight: 600, fontSize: 13, cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <Upload size={14} /> Import from old software
          </button>
          <div style={{ position: 'relative', width: 340, maxWidth: '100%' }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: theme.textSubtle }} />
            <input
              placeholder="Search by name, UHID or phone…"
              value={searchTerm || ''}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 34 }}
            />
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: theme.textMuted }}>
          <User size={42} style={{ margin: '0 auto 10px', opacity: 0.3 }} />
          <div style={{ fontSize: 14, marginBottom: 18 }}>
            {searchTerm ? 'No patients match your search.' : 'No patients registered yet.'}
          </div>
          {!searchTerm && (
            <button
              onClick={goToMigration}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 22px', borderRadius: 10,
                background: theme.primary, color: 'white',
                border: 'none', fontWeight: 700, fontSize: 14,
                cursor: 'pointer', boxShadow: '0 2px 6px rgba(245,124,31,0.3)',
              }}
            >
              <Upload size={16} /> Import patients from Excel / CSV
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
          {filtered.map(p => (
            <button key={p._id} onClick={() => setSelectedPatient(p)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: 12, borderRadius: 10,
                background: theme.bg, border: `1px solid ${theme.border}`,
                cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.background = theme.primaryLight; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.background = theme.bg; }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
                color: 'white', fontWeight: 700, fontSize: 15,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                {(p.name || '?').charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.name}
                </div>
                <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{p.uhid}</span>
                  {p.phone && <><span>·</span><Phone size={11} /><span>{p.phone}</span></>}
                </div>
              </div>
              <ChevronRight size={16} color={theme.textSubtle} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatientsPage;
